import { Flex, Form, message, Skeleton } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { Text, Title } from 'components/mvp-typography';
import { QUESTIONS_CLINIC_EDIT } from 'features/clinic/clinic-info-edit/_components/form-questions';
import { FormQuestionGroupDisplay } from 'features/clinic/clinic-info-edit/display-form-question';
import { Clinic } from 'lib/documents/clinic';
import {
  useGetClinicAllInfo,
  useMutateClinicInfo
} from 'lib/hooks/tanstack/use-getClinicInfo';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useEffect, useState } from 'react';

interface ClinicAddressForm
  extends Pick<
    Clinic,
    | 'name'
    | 'address'
    | 'addressLine2'
    | 'city'
    | 'provinceOrState'
    | 'postalOrZipCode'
    | 'country'
    | 'phoneNumber'
    | 'faxNumber'
  > {}
const BILLINGINFO_QUESTIONS = QUESTIONS_CLINIC_EDIT.filter(
  _ => _.id !== 'activate-assessment-in-clinic'
);
export function BillingInfo() {
  const clinicianContext = useClinicianContext();

  const { clinicInfo, query: clinicInfoQuery } = useGetClinicAllInfo(
    clinicianContext.currentClinic
  );
  const mutateClinicInfo = useMutateClinicInfo({
    onSuccess: () => {
      message.success('Updated organisation information');
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm<ClinicAddressForm>();

  useEffect(() => {
    if (clinicInfo) {
      form.setFieldsValue({
        name: clinicInfo.clinic.name,
        address: clinicInfo.clinic.address,
        addressLine2: clinicInfo.clinic.addressLine2,
        city: clinicInfo.clinic.city,
        provinceOrState: clinicInfo.clinic.provinceOrState,
        postalOrZipCode: clinicInfo.clinic.postalOrZipCode,
        country: clinicInfo.clinic.country,
        faxNumber: clinicInfo.clinic.faxNumber
      });
    }
  }, [clinicInfo, form]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      if (!clinicInfo?.clinic) {
        console.error('Clinic info is not available.');
        return;
      }

      const updatedClinic: Clinic = {
        ...clinicInfo.clinic,
        name: values.name,
        address: values.address,
        addressLine2: values.addressLine2,
        city: values.city,
        provinceOrState: values.provinceOrState,
        postalOrZipCode: values.postalOrZipCode,
        country: values.country,
        phoneNumber: values.phoneNumber,
        faxNumber: values.faxNumber
      };
      mutateClinicInfo.mutateAsync(updatedClinic);
      setIsEditing(false);
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (clinicInfo?.clinic) {
      // Reset the form
      form.setFieldsValue({
        name: clinicInfo.clinic.name,
        address: clinicInfo.clinic.address,
        addressLine2: clinicInfo.clinic.addressLine2,
        city: clinicInfo.clinic.city,
        provinceOrState: clinicInfo.clinic.provinceOrState,
        postalOrZipCode: clinicInfo.clinic.postalOrZipCode,
        country: clinicInfo.clinic.country,
        phoneNumber: clinicInfo.clinic.phoneNumber,
        faxNumber: clinicInfo.clinic.faxNumber
      });
    }
  };

  if (clinicInfoQuery.isLoading) {
    return <Skeleton active />;
  }

  if (!clinicInfo?.clinic) {
    return <div>No organisation information available.</div>;
  }

  return (
    <Flex justify="space-between" className="billing-info-container">
      <div>
        <Title level={3}>Billing address</Title>
        {isEditing ? (
          <Form
            form={form}
            initialValues={{
              name: clinicInfo.clinic.name,
              address: clinicInfo.clinic.address,
              addressLine2: clinicInfo.clinic.addressLine2,
              city: clinicInfo.clinic.city,
              provinceOrState: clinicInfo.clinic.provinceOrState,
              postalOrZipCode: clinicInfo.clinic.postalOrZipCode,
              country: clinicInfo.clinic.country,
              phoneNumber: clinicInfo.clinic.phoneNumber,
              faxNumber: clinicInfo.clinic.faxNumber
            }}
            onFinish={handleSave}
            className="billing-info-form"
          >
            <FormQuestionGroupDisplay
              title="Organization Details"
              questions={BILLINGINFO_QUESTIONS}
            />
            <Flex gap={16}>
              <BaseButton type="primary" htmlType="submit">
                Save
              </BaseButton>
              <BaseButton type="text" onClick={handleCancel}>
                Cancel
              </BaseButton>
            </Flex>
          </Form>
        ) : (
          <div>
            <Text>{clinicInfo.clinic.name}</Text>
            <Text>{clinicInfo.clinic.address}</Text>
            {clinicInfo.clinic.addressLine2 && (
              <Text>{clinicInfo.clinic.addressLine2}</Text>
            )}
            <Text>{`${clinicInfo.clinic.city ?? 'Not set'}, ${
              clinicInfo.clinic.provinceOrState ?? 'Not set'
            } ${clinicInfo.clinic.postalOrZipCode ?? 'Not set'}`}</Text>
            <Text>
              {clinicInfo.clinic.country === 'CA' ? 'Canada' : 'United States'}
            </Text>
          </div>
        )}
      </div>
      {!isEditing && <BaseButton onClick={handleEdit}>Edit</BaseButton>}
    </Flex>
  );
}
